// contexts/AssistantContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AssistantService from '../services/assistant.service';
import ChatService from '../services/chat.service';
import { useAuth } from '../useAuth';
import { useOrganization } from './OrganizationContext';

const AssistantContext = createContext();

/**
 * Provider para gestionar estado global de Assistants
 * Ahora integrado con OrganizationContext para soportar Personal/Organization accounts
 */
export const AssistantProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.user_id;
  
  // Usar currentAccount del OrganizationContext en lugar de user.account_id
  const { currentAccount } = useOrganization();
  const accountId = currentAccount?.id;

  const [assistants, setAssistants] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [llmModels, setLLMModels] = useState([]);

  /**
   * Cargar assistants del usuario/cuenta
   * Actualizado para usar los parámetros correctos según documentación API
   */
  const loadAssistants = useCallback(async (options = {}) => {
    if (!userId || !accountId) return;

    setLoading(true);
    setError(null);
    // Limpiar asistentes inmediatamente para evitar mostrar asistentes de otros proyectos
    setAssistants([]);
    // También limpiar el asistente seleccionado ya que puede ser de otro proyecto
    setSelectedAssistant(null);

    try {
      const result = await AssistantService.listAssistants({
        ownerId: userId, // Usar ownerId según documentación
        projectId: options.projectId, // Filtro por proyecto 
        limit: options.limit || 50,
        offset: options.offset || 0, // Usar offset en lugar de skip
        sortBy: options.sortBy || 'created_at',
        sortOrder: options.sortOrder || 'desc',
      });

      if (result.success) {
        setAssistants(result.data);
      } else {
        // Mejorar el manejo de errores específicos
        let errorMessage = 'Error loading assistants';
        
        if (result.error && Array.isArray(result.error)) {
          errorMessage = result.error.join(', ');
        } else if (result.error && result.error.message) {
          errorMessage = result.error.message;
        } else if (typeof result.error === 'string') {
          errorMessage = result.error;
        }
        
        setError(errorMessage);
        console.error('Error cargando assistants:', result.error);
      }
    } catch (err) {
      setError('Error inesperado cargando assistants');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userId, accountId]);

  // Recargar assistants cuando cambie la cuenta (switching entre Personal/Organization)
  useEffect(() => {
    if (userId && accountId) {
      // Clear current assistants to avoid showing resources from previous context
      setAssistants([]);
      setSelectedAssistant(null);
      // Reload assistants for the new account
      loadAssistants();
    }
  }, [accountId, userId, loadAssistants]);

  /**
   * Crear nuevo assistant
   * Actualizado para usar los nombres de campo correctos según documentación API
   */
  const createAssistant = useCallback(async (assistantData) => {
    if (!userId || !accountId) {
      throw new Error('Usuario no autenticado');
    }

    setLoading(true);
    setError(null);

    try {
      // Usar los nombres de campo correctos según la documentación API
      const payload = {
        ...assistantData,
        createdByUserId: userId, // Campo correcto: createdByUserId (camelCase)
        accountId: accountId, // Campo correcto: accountId (camelCase)
        projectId: assistantData.projectId || null, // Campo correcto: projectId (camelCase)
      };

      const result = await AssistantService.createAssistant(payload);

      if (result.success) {
        // Agregar a la lista local
        setAssistants((prev) => [...prev, result.data]);
        return { success: true, data: result.data };
      } else {
        setError(result.error.message);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Error inesperado creando assistant';
      setError(errorMsg);
      console.error(err);
      return { success: false, error: { message: errorMsg } };
    } finally {
      setLoading(false);
    }
  }, [userId, accountId]);

  /**
   * Actualizar assistant existente
   */
  const updateAssistant = useCallback(async (assistantId, updates) => {
    setLoading(true);
    setError(null);

    try {
      const result = await AssistantService.updateAssistant(assistantId, updates);

      if (result.success) {
        // Actualizar en la lista local
        setAssistants((prev) =>
          prev.map((a) => (a.id === assistantId ? { ...a, ...result.data } : a))
        );
        
        // Si es el assistant seleccionado, actualizarlo también
        if (selectedAssistant?.id === assistantId) {
          setSelectedAssistant((prev) => ({ ...prev, ...result.data }));
        }

        return { success: true, data: result.data };
      } else {
        setError(result.error.message);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Error inesperado actualizando assistant';
      setError(errorMsg);
      console.error(err);
      return { success: false, error: { message: errorMsg } };
    } finally {
      setLoading(false);
    }
  }, [selectedAssistant]);

  /**
   * Eliminar assistant
   */
  const deleteAssistant = useCallback(async (assistantId, reason = 'Usuario eliminó el assistant') => {
    if (!userId) {
      throw new Error('Usuario no autenticado');
    }

    setLoading(true);
    setError(null);

    try {
      const result = await AssistantService.deleteAssistant(assistantId, {
        deletion_reason: reason,
        deleted_by_user_id: userId,
        confirm_deletion: true,
        soft_delete: true,
        delete_related_data: false,
      });

      if (result.success) {
        // Remover de la lista local
        setAssistants((prev) => prev.filter((a) => a.id !== assistantId));
        
        // Si era el seleccionado, limpiarlo
        if (selectedAssistant?.id === assistantId) {
          setSelectedAssistant(null);
        }

        return { success: true };
      } else {
        setError(result.error.message);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Error inesperado eliminando assistant';
      setError(errorMsg);
      console.error(err);
      return { success: false, error: { message: errorMsg } };
    } finally {
      setLoading(false);
    }
  }, [userId, selectedAssistant]);

  /**
   * Obtener assistant por ID
   */
  const getAssistant = useCallback(async (assistantId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await AssistantService.getAssistant(assistantId);

      if (result.success) {
        return { success: true, data: result.data };
      } else {
        setError(result.error.message);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Error obteniendo assistant';
      setError(errorMsg);
      console.error(err);
      return { success: false, error: { message: errorMsg } };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cargar templates disponibles
   */
  const loadTemplates = useCallback(async () => {
    try {
      const result = await AssistantService.listTemplates();
      if (result.success) {
        setTemplates(result.data);
      }
    } catch (err) {
      console.error('Error cargando templates:', err);
    }
  }, []);

  /**
   * Cargar modelos LLM disponibles
   */
  const loadLLMModels = useCallback(async () => {
    try {
      const result = await AssistantService.listLLMs();
      if (result.success) {
        setLLMModels(result.data);
      }
    } catch (err) {
      console.error('Error cargando LLM models:', err);
    }
  }, []);

  /**
   * Cargar assistants, templates y LLMs al montar o cuando cambie el contexto
   */
  useEffect(() => {
    if (userId && accountId) {
      loadAssistants();
      loadTemplates();
      loadLLMModels();
    }
  }, [userId, accountId, loadAssistants, loadTemplates, loadLLMModels]);

  /**
   * Helper para crear un assistant con configuración por defecto
   * Usa la configuración estándar de la documentación API
   */
  const createDefaultAssistant = useCallback(async (name, description, projectId = null, type = "general") => {
    if (!userId || !accountId) {
      throw new Error('Usuario no autenticado');
    }

    const defaultAssistantData = AssistantService.createDefaultAssistant({
      name,
      description,
      userId,
      accountId,
      projectId,
      type
    });

    return await createAssistant(defaultAssistantData);
  }, [userId, accountId, createAssistant]);

  const value = {
    // Estado
    assistants,
    selectedAssistant,
    loading,
    error,
    templates,
    llmModels,

    // Actions
    setSelectedAssistant,
    loadAssistants,
    createAssistant,
    createDefaultAssistant, // Nuevo helper
    updateAssistant,
    deleteAssistant,
    getAssistant,
    loadTemplates,
    loadLLMModels,
  };

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
};

/**
 * Hook para usar el AssistantContext
 */
export const useAssistantContext = () => {
  const context = useContext(AssistantContext);
  if (!context) {
    throw new Error('useAssistantContext debe usarse dentro de AssistantProvider');
  }
  return context;
};
